import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { grey10, grey75, navy } from '@zocdoc/branding/colors';
import { Modal, ModalHeader, ModalContent, ModalButtonContainer } from '@zocdoc/components/src/modal';
import { Button } from '@zocdoc/components/src/button';
import { breakpoint, ShowFor, HideFor } from '@zocdoc/components/src/grid';
import Stars from '@zocdoc/components/src/stars';
import { FontFamily } from '@zocdoc/components/src/typography';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import PatientWithPhone from '../../../../../Shared/Telehealth/images/PatientWithPhone.svg';
import ChampCertifiedDoc from '../../../../../Shared/Telehealth/images/ChampCertifiedDoc.svg';
import { PHOTO_SIZE_XXSMALL, STARS_MEDIUM_SIZE } from '../constants';
import { BRANCH_TAGS, LOCALIZED_STRINGS } from './constants';
import HideBranchJourney from '../../../../../Shared/HideBranchJourney';
import { fetchBranchLink, SendSmsForm, StyledButton, StyledTextbox } from '../../../../../Shared/Telehealth/branch';

class BaseTelehealthModal extends Component {
    static propTypes = {
        branchDeepLink: PropTypes.string,
        onClickDownload: PropTypes.func,
        onClickSendSms: PropTypes.func,
        onClickCancel: PropTypes.func,
        onChangePhoneNumberInput: PropTypes.func,
        onViewSendSmsFailure: PropTypes.func,
        onViewSendSmsSuccess: PropTypes.func,
    };

    static defaultProps = {
        onClickDownload: noop,
        onClickSendSms: noop,
        onClickCancel: noop,
        onChangePhoneNumberInput: noop,
        onViewSendSmsFailure: noop,
        onViewSendSmsSuccess: noop,
    };

    render() {
        const {
            branchDeepLink,
            intl,
            onChangePhoneNumberInput,
            onClickCancel,
            onClickSendSms,
            onViewSendSmsFailure,
            onViewSendSmsSuccess,
            ...props
        } = this.props;

        return (
            <StyledModal
                {...filterToValidAttributes(props)}
                onClickCancel={onClickCancel}
            >
                <HideBranchJourney />
                <Header>
                    <FormattedMessage {...LOCALIZED_STRINGS.ModalHeader} />
                </Header>

                <Row>
                    <InfoContainer>
                        <Row>
                            <Sketch
                                alt={intl.formatMessage(LOCALIZED_STRINGS.VideoVisitAlt)}
                                src={PatientWithPhone}
                            />
                            <InfoText>
                                <Subtitle>
                                    <FormattedMessage {...LOCALIZED_STRINGS.FastCareTitle} />
                                </Subtitle>
                                <FormattedMessage {...LOCALIZED_STRINGS.FastCareMessage} />
                            </InfoText>
                        </Row>

                        <Row>
                            <Sketch
                                alt={intl.formatMessage(LOCALIZED_STRINGS.CertifiedDoctorAlt)}
                                src={ChampCertifiedDoc}
                            />
                            <InfoText>
                                <Subtitle>
                                    <FormattedMessage {...LOCALIZED_STRINGS.CertifiedDoctorTitle} />
                                </Subtitle>
                                <FormattedMessage {...LOCALIZED_STRINGS.CertifiedDoctorMessage} />
                            </InfoText>
                        </Row>
                    </InfoContainer>

                    <HideFor
                        xsmall
                        xxsmall
                    >
                        <ReviewContainer>
                            <MediumStars rating={5} />
                            <ReviewText>
                                <FormattedMessage {...LOCALIZED_STRINGS.Review} />
                            </ReviewText>
                            <ReviewAttribution>
                                <FormattedMessage {...LOCALIZED_STRINGS.ReviewAttribution} />
                            </ReviewAttribution>
                        </ReviewContainer>
                    </HideFor>
                </Row>

                <ShowFor
                    xsmall
                    xxsmall
                >
                    <ModalButtonContainer>
                        <SmallDownloadMessage>
                            <FormattedMessage {...LOCALIZED_STRINGS.BranchDownloadDescription} />
                        </SmallDownloadMessage>
                        <form onSubmit={this.handleSubmitDownload}>
                            <Button
                                data-test="telehealth-download-button"
                                fluid
                            >
                                <FormattedMessage {...LOCALIZED_STRINGS.DownloadButtonPrompt} />
                            </Button>
                        </form>
                    </ModalButtonContainer>
                </ShowFor>

                <HideFor
                    xsmall
                    xxsmall
                >
                    <SendSmsContainer>
                        <DownloadMessage>
                            <FormattedMessage {...LOCALIZED_STRINGS.SendSmsDescription} />
                        </DownloadMessage>
                        <ModalSendSmsForm
                            branchDeepLink={branchDeepLink}
                            buttonText={LOCALIZED_STRINGS.SendSmsButtonPrompt}
                            data-test="telehealth-send-sms-form"
                            failureText={LOCALIZED_STRINGS.BranchSmsFailure}
                            loadingText={LOCALIZED_STRINGS.BranchSmsLoading}
                            onChangePhoneNumberInput={onChangePhoneNumberInput}
                            onClickSendSms={onClickSendSms}
                            onViewSendSmsFailure={onViewSendSmsFailure}
                            onViewSendSmsSuccess={onViewSendSmsSuccess}
                            smallFailureText={LOCALIZED_STRINGS.BranchSmsFailure}
                            smsText={LOCALIZED_STRINGS.BranchTextMessage}
                            successText={LOCALIZED_STRINGS.BranchSmsSuccess}
                            tags={BRANCH_TAGS}
                            title={LOCALIZED_STRINGS.BranchTextTitle}
                        />
                    </SendSmsContainer>
                </HideFor>
            </StyledModal>
        );
    }

    handleSubmitDownload = async e => {
        const { branchDeepLink, onClickDownload } = this.props;
        e.preventDefault();
        onClickDownload(e);

        try {
            const link = await fetchBranchLink({
                branchDeepLink,
                tags: BRANCH_TAGS,
            });
            window.location.assign(link);
        } catch (err) {
            // Do nothing
        }
    };
}

const StyledModal = styled(Modal)`
    ${ModalContent} {
        -webkit-overflow-scrolling: touch;
        max-height: 85vh;
        overflow-y: scroll;
    }
`;

const Header = styled(ModalHeader)`
    padding-bottom: 31px;
    text-align: center;
    span {
        font: 21px ${FontFamily.SharpSansBold};
    }

    ${breakpoint.small} {
        text-align: left;
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    position: relative;
`;

const Subtitle = styled.span`
    font: 15px/19px ${FontFamily.SharpSansBold};
`;

const Sketch = styled.img`
    height: ${PHOTO_SIZE_XXSMALL}px;
    margin-right: 15px;
    width: ${PHOTO_SIZE_XXSMALL}px;

    ${breakpoint.small} {
        margin-top: 15px;
    }
`;

const InfoText = styled.p`
    color: ${navy};
    font: 13px/18px ${FontFamily.SharpSansMedium};

    ${Subtitle}::after {
        content: '';
        display: block;
    }

    ${breakpoint.small} {
        font: 15px/24px ${FontFamily.SharpSansMedium};
    }
`;

const InfoContainer = styled.div`
    display: flex;
    flex-basis: 100%;
    flex-shrink: 0;
    flex-direction: column;

    ${Row}:not(:last-of-type) {
        margin-bottom:36px;
        ${breakpoint.small} {
            margin-bottom: 46px;
        }
    }

    ${breakpoint.small} {
        flex-basis: 50%;
    }
`;

const MediumStars = styled(Stars)`
    font-size: ${STARS_MEDIUM_SIZE}px;
`;

const ReviewText = styled.div`
    span::before {
        content: '\u000A\u201C';
    }

    span::after {
        content: '\u201D';
    }
`;

const ReviewContainer = styled.div`
    border: 1px solid ${grey10};
    flex-basis: 50%;
    flex-shrink: 0;
    font: 24px/33px ${FontFamily.KievitSlabBook};
    margin-left: 44px;
    padding: 24px;
`;

const ReviewAttribution = styled.div`
    color: ${grey75};
    font: 15px ${FontFamily.KievitSlabBook};
    margin-top: 10px;
`;

const SmallDownloadMessage = styled.div`
    font: 13px ${FontFamily.SharpSansMedium};
    margin-bottom: 9px;
    text-align: center;
`;

const SendSmsContainer = styled.div`
    background-color: ${grey10};
    margin-top: 42px;
    padding: 24px;

    ${Row} {
        margin-top: 15px;
    }
`;

const ModalSendSmsForm = styled(SendSmsForm)`
    align-items: stretch;
    display: flex;
    margin-top: 15px;

    ${StyledButton} {
        flex-basis: 40%;
        margin-left: 10px;
        margin-right: 12px;
    }

    ${StyledTextbox} {
        border: 1px solid ${grey75};
        padding: 11px 15px 11px 24px;
    }
`;

const DownloadMessage = styled.div`
    font: 18px/25px ${FontFamily.SharpSansSemibold};
`;

export default injectIntl(BaseTelehealthModal);
